"use client";

/**
 * EmptyState — Unified empty state component for the entire application.
 *
 * Renders a domain-specific Lucide icon with a title and description.
 * Designed to replace NoDataFound, CardEmptyState, and all ad-hoc inline
 * empty states with a single, consistent component.
 *
 * @example
 * // Standalone
 * <EmptyState variant="analyses" />
 *
 * // In a table row
 * <EmptyState variant="search" inTable colSpan={6} />
 *
 * // With custom copy and action
 * <EmptyState
 *   variant="samples"
 *   title="No samples yet"
 *   description="Create your first sample to get started."
 *   action={<Button>Add Sample</Button>}
 * />
 *
 * // Compact card usage
 * <EmptyState variant="dashboard" size="sm" />
 *
 * // Icon only (inline empty visuals)
 * <EmptyStateIcon variant="samples" size="sm" />
 *
 * // Convenience wrappers
 * <EmptyAnalyses />
 * <EmptySearch />
 */

import {
  BarChart3,
  Building2,
  Cable,
  ClipboardList,
  CreditCard,
  Filter,
  FlaskConical,
  History,
  Inbox,
  LayoutDashboard,
  Microscope,
  Search,
  Stone,
  TestTube,
  Users,
  type LucideIcon,
} from "lucide-react";
import React from "react";

import { Heading } from "@/components/ui/primitives/heading";
import { Text } from "@/components/ui/primitives/text";

// ---------------------------------------------------------------------------
// Variant configuration
// ---------------------------------------------------------------------------

export type EmptyStateVariant =
  | "analyses"
  | "samples"
  | "labs"
  | "locations"
  | "dashboard"
  | "reports"
  | "integrations"
  | "search"
  | "filter"
  | "users"
  | "billing"
  | "history"
  | "aggregates"
  | "statistics"
  | "general";

interface VariantConfig {
  icon: LucideIcon;
  title: string;
  description: string;
}

const VARIANT_CONFIG: Record<EmptyStateVariant, VariantConfig> = {
  analyses: {
    icon: Microscope,
    title: "No analyses yet",
    description: "Get started by creating your first analysis.",
  },
  samples: {
    icon: TestTube,
    title: "No samples found",
    description: "Try adjusting your search criteria or add a new sample.",
  },
  labs: {
    icon: FlaskConical,
    title: "No labs assigned yet",
    description:
      "Assign a lab to start managing testing coverage for this location.",
  },
  locations: {
    icon: Building2,
    title: "No facilities found",
    description: "No facilities match your current search or filters.",
  },
  dashboard: {
    icon: LayoutDashboard,
    title: "No dashboards yet",
    description: "Create a dashboard to visualize your data.",
  },
  reports: {
    icon: ClipboardList,
    title: "No reports yet",
    description: "Reports you generate will appear here.",
  },
  integrations: {
    icon: Cable,
    title: "No integrations configured",
    description: "Connect external systems to push and pull data.",
  },
  search: {
    icon: Search,
    title: "No results found",
    description: "Try adjusting your search terms or clearing filters.",
  },
  filter: {
    icon: Filter,
    title: "No matching results",
    description:
      "No items match your current filters. Try adjusting your criteria.",
  },
  users: {
    icon: Users,
    title: "No users found",
    description: "No users match your current search or filters.",
  },
  billing: {
    icon: CreditCard,
    title: "No billing data yet",
    description: "Invoices and payment history will appear here.",
  },
  history: {
    icon: History,
    title: "No activity yet",
    description: "Actions and events will be recorded here.",
  },
  aggregates: {
    icon: Stone,
    title: "No aggregates found",
    description: "No aggregates match your current search or filters.",
  },
  statistics: {
    icon: BarChart3,
    title: "No data available",
    description: "Statistics will appear here once data is available.",
  },
  general: {
    icon: Inbox,
    title: "No data found",
    description: "There is no data to display at this time.",
  },
};

// ---------------------------------------------------------------------------
// Size mapping
// ---------------------------------------------------------------------------

type SizeKey = "xs" | "sm" | "md" | "lg";

const ICON_SIZE_CLASSES: Record<SizeKey, string> = {
  xs: "h-8 w-8",
  sm: "h-9 w-9",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EmptyStateProps {
  /** Determines the empty-state visual, default title, and default description. */
  readonly variant?: EmptyStateVariant;
  /** Override the default title. */
  readonly title?: string;
  /** Override the default description. */
  readonly description?: string;
  /** Optional action (e.g. a button) rendered below the description. */
  readonly action?: React.ReactNode;
  /** Controls the overall size of the illustration and spacing. */
  readonly size?: SizeKey;
  /** When true, wraps the content in a `<tr>/<td>` for use inside a `<tbody>`. */
  readonly inTable?: boolean;
  /** Number of columns to span when `inTable` is true. */
  readonly colSpan?: number;
  /** Tailwind min-height class, useful for card layouts to prevent collapse. */
  readonly minHeight?: string;
  /** Additional CSS classes applied to the outermost wrapper. */
  readonly className?: string;
}

export interface EmptyStateIconProps {
  readonly variant: EmptyStateVariant;
  readonly size?: SizeKey;
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

const SIZE_CLASSES: Record<
  SizeKey,
  { container: string; heading: string; text: string }
> = {
  xs: { container: "py-6", heading: "text-sm", text: "text-xs" },
  sm: { container: "py-8", heading: "text-sm", text: "text-xs" },
  md: { container: "py-12", heading: "text-base", text: "text-sm" },
  lg: { container: "py-16", heading: "text-lg", text: "text-base" },
};

export function EmptyStateIcon({
  variant,
  size = "md",
  className = "",
}: EmptyStateIconProps) {
  const Icon = VARIANT_CONFIG[variant].icon;
  return (
    <Icon
      className={`${ICON_SIZE_CLASSES[size]} text-gray-400 ${className}`.trim()}
      strokeWidth={1.5}
      aria-hidden="true"
    />
  );
}

export function EmptyState({
  variant = "general",
  title,
  description,
  action,
  size = "md",
  inTable = false,
  colSpan,
  minHeight,
  className = "",
}: EmptyStateProps) {
  const config = VARIANT_CONFIG[variant];
  const finalTitle = title ?? config.title;
  const finalDescription = description ?? config.description;
  const sizes = SIZE_CLASSES[size];

  const content = (
    <div
      className={`text-center ${sizes.container} ${minHeight ?? ""} ${className}`.trim()}
      role="status"
      aria-live="polite"
    >
      <div className="flex justify-center mb-4">
        <EmptyStateIcon variant={variant} size={size} />
      </div>

      <Heading level={3} className={`mb-1 ${sizes.heading}`}>
        {finalTitle}
      </Heading>

      <Text className={`max-w-md mx-auto break-words ${sizes.text}`}>
        {finalDescription}
      </Text>

      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );

  if (inTable) {
    return (
      <tr>
        <td className="text-center bg-gray-50/50" colSpan={colSpan}>
          {content}
        </td>
      </tr>
    );
  }

  return content;
}

// ---------------------------------------------------------------------------
// Convenience wrappers
// ---------------------------------------------------------------------------

type ConvenienceProps = Omit<EmptyStateProps, "variant">;

export const EmptyAnalyses = (props: ConvenienceProps) => (
  <EmptyState variant="analyses" {...props} />
);
export const EmptySamples = (props: ConvenienceProps) => (
  <EmptyState variant="samples" {...props} />
);
export const EmptyLabs = (props: ConvenienceProps) => (
  <EmptyState variant="labs" {...props} />
);
export const EmptyLocations = (props: ConvenienceProps) => (
  <EmptyState variant="locations" {...props} />
);
export const EmptyFacilities = EmptyLocations;
export const EmptyDashboard = (props: ConvenienceProps) => (
  <EmptyState variant="dashboard" {...props} />
);
export const EmptyReports = (props: ConvenienceProps) => (
  <EmptyState variant="reports" {...props} />
);
export const EmptyIntegrations = (props: ConvenienceProps) => (
  <EmptyState variant="integrations" {...props} />
);
export const EmptySearch = (props: ConvenienceProps) => (
  <EmptyState variant="search" {...props} />
);
export const EmptyFilter = (props: ConvenienceProps) => (
  <EmptyState variant="filter" {...props} />
);
export const EmptyUsers = (props: ConvenienceProps) => (
  <EmptyState variant="users" {...props} />
);
export const EmptyBilling = (props: ConvenienceProps) => (
  <EmptyState variant="billing" {...props} />
);
export const EmptyHistory = (props: ConvenienceProps) => (
  <EmptyState variant="history" {...props} />
);
export const EmptyAggregates = (props: ConvenienceProps) => (
  <EmptyState variant="aggregates" {...props} />
);
export const EmptyStatistics = (props: ConvenienceProps) => (
  <EmptyState variant="statistics" {...props} />
);
